<template>
  <div>
    <h2 class="title">
      <img class="title-icon"
          alt="CCS"
          :src="titleIcon">
      {{ title }}
      <i class="refresh-icon fas fa-sync-alt"
          @click="onRefreshClick()" />
    </h2>
    <BaseTableList
        class="notice-table"
        :table-set-list="tableSetList"
        :display-items="notices"
        :empty-text="emptyText"
        :default-sort="defaultSort"
        height="350"
        @sort-change="onSortChange">
      <template
          slot="message"
          slot-scope="{ row }">
        <template v-if="hasLink(row.row)">
          <router-link
              :to="{ path: getLink(row.row) }">
            <span class="link-message"
                @click="onLinkClick(row.row)">{{ getMessage(row.row) }}</span>
          </router-link>
        </template>
        <template v-else>
          {{ getMessage(row.row) }}
        </template>
      </template>
    </BaseTableList>
  </div>
</template>

<style scoped>
.title {
  margin: 0;
  border-top: 4px solid #2ea4e1;
  font-size: 16px;
  letter-spacing: .1em;
  color: #343352;
  padding: 15px 10px 15px 30px;
}

.title-icon {
    display: inline-block;
    margin-right: 12px;
    position: relative;
    top: 3px;
}

.notice-table ::v-deep th,
.notice-table ::v-deep td {
  vertical-align: middle;
  line-height: 32px;
  font-size: 15px;
}
.notice-table ::v-deep .el-tag {
  width: 80%;
}

.link-message {
  font-weight: bold;
}
.dashboard-block .refresh-icon,
.dashboard-block-title .refresh-icon {
    display: inline-block;
    position: absolute;
    top:18px;
    right: 15px;
    cursor : pointer;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'

import * as define from '@/define'
import common from '@/utils/common'
import * as queries from '@/queries/dashboard'

import mixin from '@/components/mixins/mixin'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableList from '@/components/molecules/BaseTableList'

const TAG = {
  system: {   // システム
    code: 1,
    tagInfo: [{ label: 'システム' }],
    filterInfo: { text: 'システム', value: 1 }
  },
  shipment: { // 出荷
    code: 2,
    tagInfo: [{ label: '出荷', type: 'success' }],
    filterInfo: { text: '出荷', value: 2 }
  },
  payment: {  // 決済
    code: 3,
    tagInfo: [{ label: '決済', type: 'danger' }],
    filterInfo: { text: '決済', value: 3 }
  },
  order: {    // 注文
    code: 4,
    tagInfo: [{ label: '注文', type: 'warning' }],
    filterInfo: { text: '注文', value: 4 }
  }
}

export default {
  name: 'NoticeTable',
  components: {
    BaseTableList
  },
  mixins: [ mixin ],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  props: {
    type: {
      type: Number,
      default: null,
      validator: function (value) {
        return [null, 1, 2].indexOf(value) !== -1
      }
    }
  },
  data () {
    return {
      notices: []
    }
  },
  computed: {
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition'
    }),
    tableSetList () {
      return [
        {
          type: 'String', label: 'カテゴリ', path: this.getCategoryTag, name: 'category',
          elementType: ELEMENT_TYPE.TAG, class: 'center', width: 200,
          sortable: true, sortMethod: this.categorySortMethod, columnKey: 'category',
          filters: this.getCategoryFilters, filterMethod: this.categoryFilterMethod,
          funcId: this.type === 2 ? define.PermissionKeys.Dashboard.CustomerAlert.CATEGORY
            : define.PermissionKeys.Dashboard.Information.CATEGORY
        }, {
          type: 'Slot', label: 'メッセージ', path: 'message'
        }, {
          type: 'Date', label: '日時', path: 'createdAt',
          class: 'center', width: 200, sortable: true, sortBy: 'createdAt',
          funcId: this.type === 2 ? define.PermissionKeys.Dashboard.CustomerAlert.DATETIME
            : define.PermissionKeys.Dashboard.Information.DATETIME
        }
      ]
    },
    title () {
      switch (this.type) {
        case 2:
          return 'カスタマーアラート'
        case 1:
        default:
          return 'インフォメーション'
      }
    },
    titleIcon () {
      switch (this.type) {
        case 2:
          return require('@/assets/img/dash-icon2.png')
        case 1:
        default:
          return require('@/assets/img/dash-icon1.png')
      }
    },
    emptyText () {
      switch (this.type) {
        case 1:
          return 'お知らせはありません'
        case 2:
          return 'アラートはありません'
        default:
          return ''
      }
    },
    tagCodes () {
      return _.chain(TAG)
        .map(val => val.code)
        .value()
    },
    defaultSort () {
      return this.sortCondition(this.sortConditionPath)
    },
    sortConditionPath () {
      return `noticeTableSort.${this.type}`
    }
  },
  created () {
    this.getNotices()
  },
  methods: {
    ...mapActions('LocalStorage', [
      'setSortCondition'
    ]),
    /**
     * お知らせ情報一覧を取得する
     */
    async getNotices () {
      try {
        this.$loadingService.show()
        const conditions = {
          type: this.type,
          baseDateTime: moment().utc().format()
        }
        const query = common.addGraphQlCondition(queries.GRAPH_QL_QUERY_NOTICE_LIST, conditions)
        const result = await common.postGraphQl(query)
        this.notices = _.chain(result)
          .get('data.data.notices.edges', [])
          .map('node')
          .orderBy(val => moment(val.createdAt))
          .reverse()
          .value()
      } catch (error) {
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    /**
     * カテゴリタグを取得する
     * @param {Object} row お知らせ情報
     * @returns {Array} tag タグ情報配列
     */
    getCategoryTag (row) {
      return _.chain(TAG)
        .find(val => val.code === row.tag)
        .get('tagInfo', [{ label: 'その他', type: 'info' }])
        .value()
    },
    categorySortMethod (a, b) {
      if (a.tag === b.tag) {
        // 同じタグ
        return 0
      } else if (_.includes(this.tagCodes, a.tag) === _.includes(this.tagCodes, b.tag)) {
        // 異なるタグでどちらも想定するタグまたはどちらも想定しないタグ
        if (a.tag < b.tag) {
          return -1
        } else {
          return 1
        }
      } else {
        if (_.includes(this.tagCodes, a.tag)) {
          return -1
        } else {
          return 1
        }
      }
    },
    getCategoryFilters () {
      return _.chain(TAG)
        .filter(val => _.find(this.notices, notice => notice.tag === val.code))
        .map(val => val.filterInfo)
        .thru(val => {
          const other = _.find(this.notices, notice => !_.includes(this.tagCodes, notice.tag))
          if (other) {
            val.push({ text: 'その他', value: null })
          }
          return val
        })
        .value()
    },
    categoryFilterMethod (value, row) {
      if (value == null) {
        // その他フィルター
        return !_.includes(this.tagCodes, row.tag)
      } else {
        return value === row.tag
      }
    },
    hasLink (row) {
      return row.link != null && row.link !== ''
    },
    getLink (row) {
      return row.link
    },
    getMessage (row) {
      return _.replace(row.message, '/*/', (row.linkText || ''))
    },
    onLinkClick (row) {
      // TODO StorePath が Order 決め打ちなのを修正したい
      this.$_mixin_onLinkClick(-1, 'Order')
    },
    onRefreshClick () {
      this.getNotices()
    },
    onSortChange ({ column, prop, order }) {
      this.setSortCondition({
        path: this.sortConditionPath,
        sort: { prop, order }
      })
    }
  }
}
</script>
